import type { DataSeries } from '@/generated/model';
import type { TimeRange, TimeSeriesPoint } from '@/shared/charts';
import { reportMessage } from '@/integrations/sentry/errorReporting';

import { isInTimeWindow, microsToTimestamp } from '../time-window/timeWindow';
import type { TimeWindowMicros } from '../time-window/timeWindow';

type DataSample = DataSeries['samples'][number];

function isAscendingByTime(samples: DataSample[]): boolean {
  for (let index = 1; index < samples.length; index += 1) {
    if (samples[index].timeMicros < samples[index - 1].timeMicros) {
      return false;
    }
  }

  return true;
}

/**
 * Both mappers, the chart's time domain and its nearest-point lookup rely on ascending samples.
 * The endpoint contract guarantees the order, so this is an O(n) check that normally returns the
 * same array. A response that breaks the contract is sorted so the chart stays correct, and reported
 * so the backend side can be fixed.
 */
function ensureAscendingByTime(dataSeries: DataSeries): DataSample[] {
  if (isAscendingByTime(dataSeries.samples)) {
    return dataSeries.samples;
  }

  reportMessage('Diagram data series samples arrived out of order', 'warning', {
    feature: 'time-series-chart',
    operation: 'mapDataSeries',
    seriesId: dataSeries.id,
  });

  return [...dataSeries.samples].sort((a, b) => a.timeMicros - b.timeMicros);
}

/**
 * Numeric samples inside the window become chart points. Non-numeric and non-finite values are
 * skipped rather than drawn as zero: a gap is the honest representation of "no measurement".
 * Expects ascending samples (see ensureAscendingByTime).
 */
function mapNumericSamplesToPoints(samples: DataSample[], timeWindow: TimeWindowMicros): TimeSeriesPoint[] {
  const points: TimeSeriesPoint[] = [];

  for (const sample of samples) {
    if (sample.timeMicros >= timeWindow.toTimeMicros) {
      break;
    }

    if (typeof sample.value !== 'number' || !Number.isFinite(sample.value)) {
      continue;
    }

    if (!isInTimeWindow(sample.timeMicros, timeWindow)) {
      continue;
    }

    points.push({ timestamp: microsToTimestamp(sample.timeMicros), value: sample.value });
  }

  return points;
}

/**
 * Boolean samples describe a state over time; the chart wants the intervals where it is `true`.
 * The last sample before the window carries the state the window starts in, and a state still
 * active at the end of the window is closed at the window end. Expects ascending samples.
 */
function mapBooleanSamplesToRanges(samples: DataSample[], timeWindow: TimeWindowMicros): TimeRange[] {
  const { fromTimeMicros, toTimeMicros } = timeWindow;

  if (fromTimeMicros >= toTimeMicros) {
    return [];
  }

  const ranges: TimeRange[] = [];
  let activeSinceMicros: number | undefined;

  const closeRange = (endMicros: number) => {
    if (activeSinceMicros !== undefined && activeSinceMicros < endMicros) {
      ranges.push({
        startTimestamp: microsToTimestamp(activeSinceMicros),
        endTimestamp: microsToTimestamp(endMicros),
      });
    }

    activeSinceMicros = undefined;
  };

  for (const sample of samples) {
    if (typeof sample.value !== 'boolean') {
      continue;
    }

    if (sample.timeMicros >= toTimeMicros) {
      break;
    }

    // Before the window only the latest state matters; a carried-in `true` starts at the window.
    if (sample.timeMicros < fromTimeMicros) {
      activeSinceMicros = sample.value ? fromTimeMicros : undefined;
      continue;
    }

    if (sample.value) {
      activeSinceMicros ??= sample.timeMicros;
    } else {
      closeRange(sample.timeMicros);
    }
  }

  closeRange(toTimeMicros);

  return ranges;
}

type SamplesMapper<TResult> = (samples: DataSample[], timeWindow: TimeWindowMicros) => TResult;

/**
 * Mapped once per response array and window. A series toggle re-maps every selected series while
 * the samples are still the same arrays; reusing the result keeps the points' identity, which the
 * chart renderer's own per-array cache relies on. Entries go away with the response.
 */
function cacheBySamples<TResult>(
  mapSamples: SamplesMapper<TResult>
): (dataSeries: DataSeries, timeWindow: TimeWindowMicros) => TResult {
  const cache = new WeakMap<DataSample[], { timeWindow: TimeWindowMicros; result: TResult }>();

  return (dataSeries, timeWindow) => {
    const cached = cache.get(dataSeries.samples);

    if (
      cached &&
      cached.timeWindow.fromTimeMicros === timeWindow.fromTimeMicros &&
      cached.timeWindow.toTimeMicros === timeWindow.toTimeMicros
    ) {
      return cached.result;
    }

    const result = mapSamples(ensureAscendingByTime(dataSeries), timeWindow);

    cache.set(dataSeries.samples, { timeWindow, result });

    return result;
  };
}

export const getDataSeriesPoints = cacheBySamples(mapNumericSamplesToPoints);
export const getDataSeriesRanges = cacheBySamples(mapBooleanSamplesToRanges);
